import { useState, useEffect } from 'react'
import { FiX, FiLoader, FiSave } from 'react-icons/fi'
import axios from 'axios'
import toast from 'react-hot-toast'

export default function FooterStylingModal({ isOpen, onClose, website, onSuccess }) {
  const [formData, setFormData] = useState({
    backgroundColor: '#111827',
    textColor: '#FFFFFF',
    hoverBackgroundColor: '#1F2937',
    hoverTextColor: '#F9FAFB'
  })
  const [saving, setSaving] = useState(false)
  const [isPreviewHovered, setIsPreviewHovered] = useState(false)

  useEffect(() => {
    if (website && isOpen) {
      setFormData({
        backgroundColor: website.footerStyling?.backgroundColor || '#111827',
        textColor: website.footerStyling?.textColor || '#FFFFFF',
        hoverBackgroundColor: website.footerStyling?.hoverBackgroundColor || '#1F2937',
        hoverTextColor: website.footerStyling?.hoverTextColor || '#F9FAFB'
      })
    }
  }, [website, isOpen])

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    setSaving(true)
    try {
      await axios.put(`/websites/${website._id}/footer-styling`, formData)
      toast.success('Footer styling updated successfully')
      if (onSuccess) onSuccess()
      onClose()
    } catch (error) {
      console.error('Error updating footer styling:', error)
      toast.error(error.response?.data?.message || 'Failed to update footer styling')
    } finally {
      setSaving(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-5 h-5 bg-blue-100 rounded flex items-center justify-center">
              <span className="text-blue-600 text-sm">🎨</span>
            </div>
            <h2 className="text-xl font-bold text-gray-900">Footer Styling</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <FiX className="w-5 h-5" />
          </button>
        </div>

        {/* Scrollable Form */}
        <div className="flex-1 overflow-y-auto">
          <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Customize Footer Appearance</h3>
            <p className="text-sm text-gray-600 mb-6">
              Change the background and text colors of your website's footer section.
            </p>
          </div>

          {/* Background Color */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Background Color
            </label>
            <div className="flex gap-2">
              <input
                type="color"
                value={formData.backgroundColor}
                onChange={(e) => handleInputChange('backgroundColor', e.target.value)}
                className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
              />
              <input
                type="text"
                value={formData.backgroundColor}
                onChange={(e) => handleInputChange('backgroundColor', e.target.value)}
                placeholder="#111827"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Choose the background color for the footer
            </p>
          </div>

          {/* Text Color */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Text Color
            </label>
            <div className="flex gap-2">
              <input
                type="color"
                value={formData.textColor}
                onChange={(e) => handleInputChange('textColor', e.target.value)}
                className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
              />
              <input
                type="text"
                value={formData.textColor}
                onChange={(e) => handleInputChange('textColor', e.target.value)}
                placeholder="#FFFFFF"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Choose the text color for all footer elements
            </p>
          </div>

          {/* Hover Background Color */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Hover Background Color
            </label>
            <div className="flex gap-2">
              <input
                type="color"
                value={formData.hoverBackgroundColor}
                onChange={(e) => handleInputChange('hoverBackgroundColor', e.target.value)}
                className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
              />
              <input
                type="text"
                value={formData.hoverBackgroundColor}
                onChange={(e) => handleInputChange('hoverBackgroundColor', e.target.value)}
                placeholder="#1F2937"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Choose the background color when hovering over the footer
            </p>
          </div>

          {/* Hover Text Color */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Hover Text Color
            </label>
            <div className="flex gap-2">
              <input
                type="color"
                value={formData.hoverTextColor}
                onChange={(e) => handleInputChange('hoverTextColor', e.target.value)}
                className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
              />
              <input
                type="text"
                value={formData.hoverTextColor}
                onChange={(e) => handleInputChange('hoverTextColor', e.target.value)}
                placeholder="#F9FAFB"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Choose the text color when hovering over the footer
            </p>
          </div>

          {/* Preview */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Preview
            </label>
            <div
              className="border border-gray-300 rounded-lg p-4 space-y-2 cursor-pointer transition-colors"
              style={{
                backgroundColor: isPreviewHovered ? formData.hoverBackgroundColor : formData.backgroundColor,
                color: isPreviewHovered ? formData.hoverTextColor : formData.textColor
              }}
              onMouseEnter={() => setIsPreviewHovered(true)}
              onMouseLeave={() => setIsPreviewHovered(false)}
            >
              <div className="flex justify-center gap-3 text-sm">
                <span>📘</span>
                <span>📷</span>
                <span>📘</span>
              </div>
              <div className="text-center text-sm">
                © 2024 {website?.name || 'Your Website'}. All rights reserved.
              </div>
              <div className="text-center text-xs text-gray-400 mt-2">
                {isPreviewHovered ? 'Hover effect active' : 'Hover to preview hover colors'}
              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex gap-3 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
            >
              {saving ? (
                <>
                  <FiLoader className="w-4 h-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <FiSave className="w-4 h-4" />
                  Save Changes
                </>
              )}
            </button>
          </div>
        </form>
        </div>
      </div>
    </div>
  )
}
